import * as Yup from "yup";
import ValidateMessages from "../../../../../../core/models/static/texts/vallidate-messages";
import React, {useMemo, useRef, useState} from "react";
import {makeRequired, makeValidate} from "mui-rff";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {createUUId, deepCopy} from "../../../../../../core/services/utils/utils";
import Dialog from "../../../../../components/base/dialog";
import {Col, Row} from "reactstrap";
import Form from "../../../../../components/base/form";
import {OutlinedInputForm} from "../../../../../components/base/input/outlined_input";
import {OutlinedDropdownForm} from "../../../../../components/base/dropdown/outlined_dropdown";
import Button from "../../../../../components/base/button/button";
import {DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import useCountryProvinceCache from "../../../../../hooks/use-country-province-cache";
import EnvService from "../../../../../../core/services/env";

const UpsertCustomerAddressDialogV2 = ({
                                           open,
                                           close,
                                           address,
                                           editing = false,
                                           duplicating = false,
                                           hasBillingAddress = false,
                                           submit,
                                       }) => {
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();
    const formIdRef = useRef(createUUId(true));


    const schema = useMemo(() => {
        return Yup.object().shape({
            name: Yup.string().trim().nullable().required(ValidateMessages.required),
            line1: Yup.string().trim().nullable().required(ValidateMessages.required),
            line2: Yup.string().trim().nullable(),
            line3: Yup.string().trim().nullable(),
            line4: Yup.string().trim().nullable(),
            city: Yup.string().trim().nullable().required(ValidateMessages.required),
            country: Yup.string().trim().nullable().required(ValidateMessages.required),
            provState: Yup.string().trim().nullable().required(ValidateMessages.required),
            postalCode: Yup.string().trim().nullable().required(ValidateMessages.required),
            phone: Yup.string().trim().nullable(),
            email: Yup.string().trim().nullable().email("Must be a valid email"),
            fax: Yup.string().trim().nullable(),
            website: Yup.string().trim().nullable(),
            type: hasBillingAddress
                ? Yup.string().trim().nullable()
                : Yup.string().trim().nullable().required(ValidateMessages.required),
            isDefault: Yup.boolean().default(false).nullable(),
        });
    }, [hasBillingAddress])

    const validate = makeValidate(schema);
    const required = makeRequired(schema);

    const addressTypes = React.useMemo(() => ([
        {
            title: 'Shipping',
            id: 'shipping'
        },
        {
            title: 'Billing',
            id: 'billing'
        }
    ]), [])

    const initialValues = useMemo(() => ({
        name: address?.name,
        line1: address?.line1,
        line2: address?.line2,
        line3: address?.line3,
        line4: address?.line4,
        city: address?.city,
        country: address?.country, // string code
        provState: address?.provState, // string code
        postalCode: address?.postalCode,
        phone: address?.phone,
        email: address?.email,
        fax: address?.fax,
        website: address?.website,
        type: hasBillingAddress ? 'shipping' : address?.type?.toLowerCase() || 'shipping',
        isDefault: address?.isDefault || false,
    }), [address?.city, address?.country, address?.email, address?.fax, address?.isDefault, address?.line1, address?.line2, address?.line3, address?.line4, address?.name, address?.phone, address?.postalCode, address?.provState, address?.type, address?.website, hasBillingAddress])

    const availableTypes = React.useMemo(() => addressTypes.map(t => ({
        ...t,
        disabled: t.id === 'billing' && hasBillingAddress
    })).filter(e => !e.disabled), [addressTypes, hasBillingAddress])

    // Fetch all provinces at once
    const {countries} = useCountryProvinceCache(undefined, true);

    /**
     * Creates / Edits the address by invoking the [submit] callback.
     * @param values {any} the form values.
     */
    const onSubmit = async (values) => {
        setSubmitting(true);
        const copy = deepCopy(values);

        const resultFlag = await submit(copy);
        if (!isMounted())
            return;
        setSubmitting(false);
        if (!!resultFlag) {
            close();
        }
    }

    /**
     * Selects the given country and removes any values associated with the province of the form.
     * @param e
     * @param form
     */
    const onCountrySelected = (e, form) => {
        const countryCode = e.target.value;
        form.change('provState', null);
        form.change('country', countryCode);
    }

    const colClass = 'px-2 mb-3';
    return (
        <Dialog
            open={open}
            setOpen={close}
            loading={submitting}
            disableEscapeKeyDown={submitting}
            disableBackdropClick={submitting}
        >
            <DialogTitle id={'scroll-dialog-title'}>
                {
                    editing
                        ? 'Edit Address'
                        : hasBillingAddress ? `${duplicating ? "Duplicate as a" : "Add"} Shipping Address` : `${duplicating ? "Duplicate as an" : "Add"} Address`
                }
            </DialogTitle>
            <DialogContent dividers>
                <Form
                    id={formIdRef.current}
                    className='w-100'
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={initialValues}
                    render={({submitting, values, form, initialValues, errors, submitErrors}) => {
                        const selectedCountry = countries.find(e => e.code === values.country);
                        const provinces = selectedCountry ? selectedCountry.provinces : [];

                        return (
                            <Row className='no-gutters w-100'>
                                <Col xs={12} className={colClass}>
                                    <OutlinedInputForm
                                        size={'small'}
                                        label={'Name'}
                                        name={'name'}
                                        required={required.name}
                                        disabled={submitting}
                                    />
                                </Col>
                                <Col xs={12} className={colClass}>
                                    <OutlinedInputForm
                                        size={'small'}
                                        label={'Line 1'}
                                        name={'line1'}
                                        required={required.line1}
                                        disabled={submitting}
                                    />
                                </Col>
                                <Col xs={12} className={colClass}>
                                    <OutlinedInputForm
                                        size={'small'}
                                        label={'Line 2'}
                                        name={'line2'}
                                        required={required.line2}
                                        disabled={submitting}
                                    />
                                </Col>
                                <Col xs={6} className={colClass}>
                                    <OutlinedInputForm
                                        size={'small'}
                                        label={'Line 3'}
                                        name={'line3'}
                                        required={required.line3}
                                        disabled={submitting}
                                    />
                                </Col>
                                <Col xs={6} className={colClass}>
                                    <OutlinedInputForm
                                        size={'small'}
                                        label={'Line 4'}
                                        name={'line4'}
                                        required={required.line4}
                                        disabled={submitting}
                                    />
                                </Col>
                                <Col xs={6} className={colClass}>
                                    <OutlinedInputForm
                                        size={'small'}
                                        label={'City'}
                                        name={'city'}
                                        required={required.city}
                                        disabled={submitting}
                                    />
                                </Col>
                                <Col xs={6} className={colClass}>
                                    <OutlinedInputForm
                                        size={'small'}
                                        label={!selectedCountry ? 'Postal/Zip Code' : selectedCountry?.name === EnvService.USATitle ? "Zip Code" : 'Postal Code'}
                                        name={'postalCode'}
                                        required={required.postalCode}
                                        disabled={submitting}
                                    />
                                </Col>
                                <Col xs={6} className={colClass}>
                                    <OutlinedDropdownForm
                                        size={'small'}
                                        label={'Country'}
                                        onChange={(e) => onCountrySelected(e, form)}
                                        name={`country`}
                                        required={required.country}
                                        disabled={submitting}
                                        renderValue={v => countries.find(e => e.code === v)?.name}
                                        data={countries.map(e => ({
                                            value: e.code,
                                            label: e.name,
                                            id: e.code
                                        })) ?? []}
                                    />
                                </Col>
                                <Col xs={6} className={colClass}>
                                    <OutlinedDropdownForm
                                        size={'small'}
                                        label={!selectedCountry ? 'Province / State' : selectedCountry?.name === EnvService.USATitle ? "State" : 'Province'}
                                        name={`provState`}
                                        required={required.provState}
                                        disabled={submitting}
                                        renderValue={v => provinces.find(e => e.code === v)?.name ?? v}
                                        data={provinces.map(e => ({
                                            value: e.code,
                                            label: e.name,
                                            id: e.code
                                        })) ?? []}
                                    />
                                </Col>
                                <Col xs={6} className={colClass}>
                                    <OutlinedInputForm
                                        size={'small'}
                                        label={'Phone'}
                                        name={'phone'}
                                        required={required.phone}
                                        disabled={submitting}
                                    />
                                </Col>
                                <Col xs={6} className={colClass}>
                                    <OutlinedInputForm
                                        size={'small'}
                                        label={'Fax'}
                                        name={'fax'}
                                        required={required.fax}
                                        disabled={submitting}
                                    />
                                </Col>
                                <Col xs={6} className={colClass}>
                                    <OutlinedInputForm
                                        size={'small'}
                                        label={'Email'}
                                        name={'email'}
                                        required={required.email}
                                        disabled={submitting}
                                    />
                                </Col>
                                <Col xs={6} className={colClass}>
                                    <OutlinedInputForm
                                        size={'small'}
                                        label={'Website'}
                                        name={'website'}
                                        required={required.website}
                                        disabled={submitting}
                                    />
                                </Col>
                                {
                                    !editing && availableTypes.length > 1 &&
                                    <Col xs={12} className={colClass}>
                                        <OutlinedDropdownForm
                                            size={'small'}
                                            label={'Type'}
                                            name={`type`}
                                            required={required.type}
                                            disabled={submitting}
                                            renderValue={v => availableTypes.find(e => e.id === v)?.title}
                                            data={availableTypes?.map(e => ({
                                                value: e.id,
                                                label: e.title,
                                                id: e.id,
                                                disabled: e.disabled
                                            })) ?? []}
                                        />
                                    </Col>
                                }
                                {
                                    editing && initialValues.provState !== values.provState && values.type === 'shipping' &&
                                    <Col xs={12} className='align-items-center d-flex my-3'>
                                        <Typography variant={'body2'} className='text-color-3'>
                                            <Typography
                                                variant={'body2'}
                                                color={'secondary.main'}
                                                component={'span'}
                                                fontWeight={600}
                                                sx={{marginInlineEnd: 1}}
                                            >
                                                Note:
                                            </Typography>
                                            Please ensure that after editing the province of the address, you set the except numbers
                                            for any of the applied taxes.
                                        </Typography>
                                    </Col>
                                }
                            </Row>
                        );
                    }}
                />
            </DialogContent>
            <DialogActions className={'d-flex align-items-center justify-content-between p-0'}>
                <Button
                    disabled={!open || submitting}
                    variant={'dialog'}
                    color={'primary'}
                    type={'button'}
                    onClick={() => close()}
                    borderRadius={'0 8px 0 8px'}
                >
                    Close
                </Button>
                <Button
                    disabled={!open || submitting}
                    variant={'dialog'}
                    color={'primary'}
                    type={'submit'}
                    form={formIdRef.current}
                    borderRadius={'8px 0 8px 0'}
                >
                    {
                        submitting
                            ? 'Submitting...'
                            : editing
                                ? 'Edit'
                                : duplicating ? 'Duplicate' : 'Add'
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default UpsertCustomerAddressDialogV2;
